import React, { useState, useEffect, useRef } from "react";
import Section from "./layouts/Section";
import { MEMBERSHIP_CONFIG } from "../data/membershipData";
import { submitLead } from "../utils/leadSubmit";
import { track, EVENTS, trackPhoneCall } from "../utils/analytics";

/**
 * VIP+ Membership landing page (Growth Platform — Strategy #01).
 *
 * Public-facing membership experience: pricing, benefits, plan comparison and
 * a "Join VIP+" flow. Reads all pricing/benefits from membershipData.js.
 *
 * Billing is NOT live yet (MEMBERSHIP_CONFIG.billingEnabled === false), so the
 * Join CTA captures an honest waitlist lead via the shared EmailJS pipeline —
 * no fake payment, no fake "you're a member" state. When the MERN backend +
 * payment provider are live, flip the flag and swap the waitlist form for
 * real checkout; the rest of the page is unchanged.
 */

const NAVY = "#18305d";
const ACCENT = "#ffaa19";

const { program, plans, benefits, terms, billingEnabled } = MEMBERSHIP_CONFIG;

function VipMembership() {
  const [selectedPlan, setSelectedPlan] = useState(
    plans.find((p) => p.highlighted)?.id || plans[0].id
  );
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    track(EVENTS.MEMBERSHIP_VIEWED, { program: program.id });
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const canSubmit = form.name.trim() && form.email.trim();
  const plan = plans.find((p) => p.id === selectedPlan);

  const handleJoinClick = (planId) => {
    if (planId) setSelectedPlan(planId);
    setShowForm(true);
    track(EVENTS.MEMBERSHIP_JOIN_STARTED, {
      program: program.id,
      plan: planId || selectedPlan,
    });
    setTimeout(() => {
      if (formRef.current) {
        const y =
          formRef.current.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 50);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    submitLead({
      name: form.name,
      email: form.email,
      phone: form.phone,
      device: `${program.name} — ${plan.label} (${plan.priceSuffix})`,
      subject: `VIP+ Membership Waitlist: ${plan.label}`,
      message: [
        `New VIP+ membership waitlist signup.`,
        ``,
        `Plan: ${program.name} ${plan.label} — $${plan.price}${plan.priceSuffix}`,
      ].join("\n"),
    })
      .then(() => {
        track(EVENTS.MEMBERSHIP_WAITLIST_LEAD, {
          program: program.id,
          plan: plan.id,
        });
        setDone(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message:
            "Something went wrong. Please try again or call us at (215) 939-3933.",
        });
        setIsSubmitting(false);
        // eslint-disable-next-line no-console
        console.log("FAILED...", error?.text);
      });
  };

  return (
    <Section title={`${program.name} Membership`}>
      <section className="section page-content">
        <div className="container">
          {/* Hero */}
          <div className="row justify-content-center mb-40">
            <div className="col-lg-9 text-center">
              <span
                style={{
                  display: "inline-block",
                  background: "#fff9ef",
                  color: ACCENT,
                  fontWeight: 800,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  fontSize: "12px",
                  padding: "7px 14px",
                  borderRadius: "999px",
                  marginBottom: "16px",
                }}
              >
                <i className="fas fa-crown mr-2" />
                {program.name} Membership
              </span>
              <h2 className="text-dark mb-15">
                Instead of only selling repairs — join a relationship.
              </h2>
              <p className="text-muted" style={{ fontSize: "18px", maxWidth: "620px", margin: "0 auto" }}>
                {program.tagline}
              </p>
            </div>
          </div>

          {/* Pricing plans */}
          <div className="row justify-content-center mb-20">
            {plans.map((p) => {
              const active = p.id === selectedPlan;
              return (
                <div className="col-md-5 col-lg-4 mb-25" key={p.id}>
                  <div
                    onClick={() => setSelectedPlan(p.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedPlan(p.id)}
                    style={{
                      position: "relative",
                      background: active ? NAVY : "#ffffff",
                      color: active ? "#fff" : NAVY,
                      borderRadius: "18px",
                      padding: "32px 28px",
                      border: `2px solid ${active ? ACCENT : "#e6e6e6"}`,
                      boxShadow: active
                        ? "0 14px 40px rgba(24,48,93,0.28)"
                        : "0 4px 18px rgba(0,0,0,0.06)",
                      cursor: "pointer",
                      transition: "all .2s",
                      height: "100%",
                    }}
                  >
                    {p.badge && (
                      <span
                        style={{
                          position: "absolute",
                          top: "-13px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          background: ACCENT,
                          color: NAVY,
                          fontWeight: 800,
                          fontSize: "12px",
                          padding: "5px 14px",
                          borderRadius: "999px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.badge}
                      </span>
                    )}
                    <h4 style={{ color: active ? "#fff" : NAVY, marginBottom: "10px" }}>
                      {p.label}
                    </h4>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                      <span style={{ fontSize: "40px", fontWeight: 800 }}>
                        ${p.price}
                      </span>
                      <span style={{ opacity: 0.75, fontWeight: 600 }}>
                        {p.priceSuffix}
                      </span>
                    </div>
                    <p style={{ margin: "8px 0 20px", fontSize: "14px", opacity: 0.8 }}>
                      {p.note}
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJoinClick(p.id);
                      }}
                      style={{ padding: "12px" }}
                    >
                      Join {program.name}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Honest note that billing isn't live yet */}
          {!billingEnabled && (
            <div className="row justify-content-center mb-40">
              <div className="col-lg-8 text-center">
                <p className="text-muted" style={{ fontSize: "14px" }}>
                  <i className="fas fa-info-circle mr-2" style={{ color: ACCENT }} />
                  {program.name} is launching soon. Join the waitlist below and
                  we'll contact you to activate your membership — no payment is
                  taken online yet.
                </p>
              </div>
            </div>
          )}

          {/* Benefits */}
          <div className="row justify-content-center mb-20">
            <div className="col-lg-10">
              <h3 className="text-dark text-center mb-30">
                Everything included with {program.name}
              </h3>
              <div className="row">
                {benefits.map((b) => (
                  <div className="col-sm-6 col-lg-4 mb-20" key={b.text}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        background: "#f6f8fc",
                        borderRadius: "12px",
                        padding: "16px 18px",
                        height: "100%",
                      }}
                    >
                      <span
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "10px",
                          background: "#fff",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        }}
                      >
                        <i className={b.icon} style={{ color: ACCENT }} />
                      </span>
                      <span style={{ fontWeight: 600, color: NAVY, fontSize: "15px" }}>
                        {b.text}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Join / waitlist form */}
          <div className="row justify-content-center" ref={formRef}>
            <div className="col-lg-7">
              {done ? (
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "16px",
                    padding: "40px 30px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: "#d4edda",
                      color: "#155724",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 18px",
                      fontSize: "30px",
                    }}
                  >
                    <i className="fas fa-check" />
                  </div>
                  <h4 className="text-dark mb-10">You're on the list!</h4>
                  <p className="text-muted mb-20" style={{ maxWidth: "440px", margin: "0 auto 20px" }}>
                    Thanks {form.name.split(" ")[0] || "there"}! We'll contact you
                    to activate your {program.name} {plan.label} membership as soon
                    as it launches.
                  </p>
                  <a
                    href="tel:+12159393933"
                    className="btn btn-primary"
                    onClick={() => trackPhoneCall("vip_success")}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    Questions? Call (215) 939-3933
                  </a>
                </div>
              ) : showForm ? (
                <div
                  style={{
                    background: NAVY,
                    borderRadius: "16px",
                    padding: "34px",
                    boxShadow: "0 10px 40px rgba(24,48,93,0.25)",
                  }}
                >
                  <h4 className="text-white mb-8">
                    Join {program.name} — {plan.label}
                  </h4>
                  <p style={{ color: "rgba(255,255,255,0.8)" }} className="mb-25">
                    ${plan.price}
                    {plan.priceSuffix}. Enter your details and we'll reach out to
                    activate your membership.
                  </p>

                  {status.message && (
                    <div
                      style={{
                        padding: "12px 16px",
                        borderRadius: "8px",
                        marginBottom: "18px",
                        background: "#f8d7da",
                        color: "#721c24",
                      }}
                    >
                      <i className="fas fa-exclamation-circle mr-2" />
                      {status.message}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">Name *</label>
                        <input
                          className="form-control py-3 px-3"
                          type="text"
                          required
                          placeholder="Your name"
                          value={form.name}
                          onChange={(e) => update({ name: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">Email *</label>
                        <input
                          className="form-control py-3 px-3"
                          type="email"
                          required
                          placeholder="you@email.com"
                          value={form.email}
                          onChange={(e) => update({ email: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">
                          Phone (optional)
                        </label>
                        <input
                          className="form-control py-3 px-3"
                          type="tel"
                          placeholder="(215) 555-1234"
                          value={form.phone}
                          onChange={(e) => update({ phone: e.target.value })}
                        />
                      </div>
                      <div className="form-group mb-20 col-md-6">
                        <label className="mb-2 font-w-600 text-white">Plan</label>
                        <select
                          className="form-control py-3 px-3"
                          value={selectedPlan}
                          onChange={(e) => setSelectedPlan(e.target.value)}
                        >
                          {plans.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.label} — ${p.price}
                              {p.priceSuffix}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={isSubmitting || !canSubmit}
                      style={{ padding: "13px 34px" }}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2" />
                          Joining...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-crown mr-2" />
                          Join the {program.name} Waitlist
                        </>
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleJoinClick(selectedPlan)}
                    style={{ padding: "15px 40px", fontSize: "17px" }}
                  >
                    <i className="fas fa-crown mr-2" />
                    Join {program.name} — ${plan.price}
                    {plan.priceSuffix}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Terms */}
          <div className="row justify-content-center mt-40">
            <div className="col-lg-9 text-center">
              <p className="text-muted" style={{ fontSize: "12px" }}>
                {terms}
              </p>
            </div>
          </div>
        </div>
      </section>
    </Section>
  );
}

export default VipMembership;
